// A section detail card, ported from sections/sub_items.coffee SectionCardView + the erb
// #mn-section-detail-template: crop thumbnail with hover-preview/click-zoom, "SQ{n}: text"
// title, Expand/Collapse strip and the 7-row detail table. The row it lives in is what
// toggles it -- no filter dispatch here, that lives on the sections table labels.

import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import { processText } from '@aytm/app/stats/page/components/lib/utils'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import ImagePreview from '@aytm/app/stats/components/image-preview/ImagePreview'

import { calcSectionCardTable } from '@aytm/app/stats/page/question-views/mn/components/sections-section/lib/sectionCardTable'
import styles from '@aytm/app/stats/page/question-views/mn/components/sections-section/section-cards/styles/SectionCards.module.scss'

// OQ-4: the raw crop URL serves as BOTH the preview and the zoom image — the coffee widget
// deliberately bypassed the resize pipeline for section crops (256×256 preview cap); the
// thumb is a plain <img> under a hover Popup (the ImagePopup wrapper shape minus its
// asset-path rewrite), so no loader ever renders at thumb size
const PREVIEW_MIN = '70px'
const PREVIEW_MAX = '256px'

// The row a section shows while it is closed: the crop it was cut from, what it is called, and
// the expander. The hover preview belongs here — it is opened by this row's own thumbnail.
export const SectionCardHeader = ({ section, image, collapsed, expandable = true }) => {
  const cropUrl = image?.original ? window.$ASSETS?.getCropAssetPath?.(section.border, image.original.path) : null

  return (
    <div className={styles.description}>
      {cropUrl ? (
        // the popup opens on hover while the row toggles on click, so the click is stopped here
        // to keep inspecting a crop from collapsing the row
        <div
          id={`asset-${image.id}-sq${section.position}`}
          className={styles.thumbnail}
          onClick={event => event.stopPropagation()}
        >
          <Popup
            className="PRAYTMTheme rfd-ui-popup-image"
            trigger="hover"
            direction="left"
            portal
            hideOtherPopupsOnShow
            content={() => (
              <ImagePreview
                minWidth={PREVIEW_MIN}
                maxWidth={PREVIEW_MAX}
                minHeight={PREVIEW_MIN}
                maxHeight={PREVIEW_MAX}
                imageUrl={cropUrl}
                previewUrl={cropUrl}
              />
            )}
          >
            <img src={cropUrl} alt="" />
          </Popup>
        </div>
      ) : null}

      <span
        className={styles.title}
        // processText marks up logic, piping and aliases, so the title carries its html
        dangerouslySetInnerHTML={{ __html: `SQ${section.position}: ${processText(section.text)}` }}
      />

      {expandable ? (
        <span className={classNames(styles.expand, { [styles.expandOpen]: !collapsed })}>
          <i className="icon-aytmfd-chevron-bracket" />
        </span>
      ) : null}
    </div>
  )
}

// What an open section expands to: the detail table for the slice of the answer that is its own.
export const SectionCardBody = ({ section, answer, questionId, questionTotal, priceTemplate }) => (
  <Table {...calcSectionCardTable({ questionId, position: section.position, answer, priceTemplate, questionTotal })} />
)

SectionCardHeader.displayName = 'SectionCardHeader'
SectionCardBody.displayName = 'SectionCardBody'

const SECTION_PROP_TYPE = PropTypes.shape({
  position: PropTypes.number,
  text: PropTypes.string,
  border: PropTypes.object
})

SectionCardHeader.propTypes = {
  section: SECTION_PROP_TYPE.isRequired,
  image: PropTypes.object,
  collapsed: PropTypes.bool,
  expandable: PropTypes.bool
}

SectionCardBody.propTypes = {
  section: SECTION_PROP_TYPE.isRequired,
  answer: answerPropType,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionTotal: PropTypes.number,
  priceTemplate: PropTypes.string
}
